import "@fontsource/space-grotesk/400.css";
import "@fontsource/space-grotesk/500.css";
import "@fontsource/space-grotesk/600.css";
import "@fontsource/space-grotesk/700.css";
import "@fontsource/jetbrains-mono/400.css";
import "@fontsource/jetbrains-mono/500.css";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { ErrorScreen } from "./features/shell/ErrorScreen";
import { startBoardBridge } from "./lib/boardBridge";
import { installErrorCapture } from "./lib/fatal";
import { parseSearch, stringifySearch } from "./lib/search";
import { routeTree } from "./routeTree.gen";

// Before the first render, so a throw from a handler or an unawaited promise is still recoverable evidence.
installErrorCapture();

// Only does anything when this page is inside a Blueprint frame and the server put
// `?board=1` on it. Started before the first render so the very first click in a frame
// is reported rather than performed.
startBoardBridge();

const router = createRouter({
  routeTree,
  // A preview build is served from /_p/<sha>/, not from the root, and the router has to
  // know or every route it matches and every link it builds is one level up — the first
  // click inside a board frame would leave the preview and land on production. Vite
  // hands the same value it used for asset URLs through BASE_URL, so the two can never
  // disagree; on the real deploy it is "/", which the router treats as no basepath.
  basepath: import.meta.env.BASE_URL,
  defaultPreload: "intent",
  scrollRestoration: false,
  defaultErrorComponent: ErrorScreen,
  parseSearch,
  stringifySearch,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

createRoot(document.getElementById("root") as HTMLElement).render(
  <StrictMode>
    <RouterProvider router={router} />
  </StrictMode>,
);
